import { Feature } from '@tencent/workbench';
import BaseToolbarFeature from '@framework/workbench/common/feature/base-toolbar-feature';
import {
    ChartModelFactory,
} from '@src/slide/domain/ui-model/element-modeles/chart-models/chart-presets/chart-model-factory';
import { canvasToEmu } from '@src/slide/infrastructure/module/independent/unit';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import { EventCodeType, TDWActionType, TDWModuleType, TDWOperationType } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import { config, insertChartTypeMap } from './config';
import ChartModel from '@src/slide/domain/ui-model/element-modeles/chart-models/chart-model';
import { DataType } from '@tencent/slide-sdk-model';
import { IPcEditorAppServices } from '@src/slide/application/entry/toc/editor/pc/pc-editor-app-services.interface';
import { ActionType } from '@src/framework/editor/src/js/constants/common-action-type';
import { FIXED_KEYS_DOUBLE } from '../../schema/constant';
import userLocalStorage, { LocalStorageKeys } from '@src/slide/infrastructure/module/independent/local-storage';
import { testInsertChart } from '@src/slide/domain/ui-model/element-modeles/chart-models';

@Feature()
export class ChartFeature extends BaseToolbarFeature {
    id = config.id;
    config = config;
    constructor() {
        super();
        // @ts-ignore
        window.testInsertChart = testInsertChart;
    }

    public insertChart(name: string, path: any[]) {
        const createChartType = insertChartTypeMap[name];

        const chartUiData = ChartModelFactory.createChartDataByType(createChartType);
        const chartSdkData: any = {};
        ChartModel.convertDataUiToSdk(chartUiData, chartSdkData, {});

        const delElements = [];
        const placeHolderRect = [];
        const rect = this.getDefaultRect(placeHolderRect, delElements);

        this.insertElement(
            {
                ...chartSdkData,
                spPr: {
                    xfrm: {
                        ext: {
                            cx: canvasToEmu(rect.width),
                            cy: canvasToEmu(rect.height),
                        },
                        off: {
                            x: canvasToEmu(rect.left),
                            y: canvasToEmu(rect.top),
                        },
                    },
                },
                type: DataType.GraphicalObjectFrameChart,
            }
            , delElements,
        );

        // 打开数据编辑窗口
        this.getAppService().editableServiceLayer!.chartManager.openChartDataEditor(this.getSelectModel());
        this.showThemeTips();

        this.tdwReport(path, JSON.stringify({
            type: createChartType,
        }));
    }

    /**
     * 插入图表时，展示下主题色切换引导
     * 一个客户端弹一次
     * @private
     */
    private showThemeTips() {
        const shown = userLocalStorage.getItem(LocalStorageKeys.SLIDE_LOCAL_CHART_TIPS_SHOW);
        if (shown) {
            return;
        }
        userLocalStorage.setItem(LocalStorageKeys.SLIDE_LOCAL_CHART_TIPS_SHOW, '1');
        this.getAppService().editableServiceLayer?.themeColorManager.showThemeTips();
    }

    private tdwReport(path: any[], createChartType: string) {
        const reportInfo =  {
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.TOOLBAR_INSERT_TAB,
            action: TDWActionType.CHART,
            eventcode: EventCodeType.DOC_PPT_TOOLBAR_INSERT_TAB_CHART,
        };
        if (path?.includes(ActionType.SINGLE_TOOLBAR_MENU)) {
            tdw({
                ...reportInfo,
                ver3: 1,
                ver5: createChartType,
            });
        } else if (path?.includes(FIXED_KEYS_DOUBLE.TOOLBAR_INSERT)) {
            tdw({
                ...reportInfo,
                ver3: 2,
                ver5: createChartType,
            });
        } else {
            tdw({
                opername: TDWOperationType.DOC_PPT,
                module: TDWModuleType.CHART,
                action: TDWActionType.INSERT_CHART,
                ver6: createChartType,
            });
        }
    }

    private insertElement(sdkData, delElements) {
        const { currentSlideIndex } = this.getSelectionService();
        const slideModel = this.getSelectionService().slides[currentSlideIndex];

        if (!slideModel) return;

        if (delElements.length > 0) {
            const addNewEleInfo: any = {};
            addNewEleInfo.eleType = UIModelType.CHART;
            addNewEleInfo.eleProps = sdkData;

            slideModel.deletePlaceHolderAndAddNewElements(delElements, [addNewEleInfo]);
        } else {
            // slideModel.sdkData.addShapes([sdkData]);
            slideModel.addElement(UIModelType.CHART, sdkData);
        }
    }

    private getDefaultRect(placeHolderRect, delElements) {
        this.insertToSelectedPlaceHolderIfExist(placeHolderRect, delElements);
        if (placeHolderRect.length === 1) {
            return placeHolderRect[0];
        }

        const { slideWidth, slideHeight } = this.getUiDataService().presentation.props;
        const { width, height } = this.getDefaultSize();
        const left = (slideWidth / 2) - (width / 2);
        const top = (slideHeight / 2) - (height / 2);

        return {
            left,
            top,
            width,
            height,
        };
    }

    // firstStep: 如果有选中的表格占位符，优先插入
    private insertToSelectedPlaceHolderIfExist = (rect, delElements) => {
        const selectedUIModelArr = this.getSelectionService().selectedElements.selectedArray;
        const selectedPhAr = this.getAllSeletedModelsAcceptPic(selectedUIModelArr);
        if (!selectedPhAr || typeof (selectedPhAr) === 'undefined' || selectedPhAr.length < 1) {
            return false;
        }
        rect.push(selectedPhAr[0].props.style);
        delElements.push(selectedPhAr[0]);
        return true;
    };

    // 是否是可以插入表格的占位符
    private isPlaceHolderAcceptPic = model => (model.isPlaceholder && !model.isCustomPlaceholder
        && (typeof (model.placeHolder.type) === 'undefined'));

    private getAllSeletedModelsAcceptPic = (models) => {
        if (!models || typeof (models) === 'undefined' || models.length < 1) {
            return [];
        }
        // 过滤选中元素
        const selectedModels =  models.filter(model => this.isPlaceHolderAcceptPic(model));
        return selectedModels;
    };

    private getDefaultSize() {
        const uiDataService = (AppServiceProvider.getInstance().getAppServices() as IPcEditorAppServices)
            .getUiDataService();
        const scaleW = uiDataService.presentation.props.slideWidth / 1920;
        const scaleH = uiDataService.presentation.props.slideHeight / 1080;
        return {
            width: 1280 * scaleW,
            height: 853 * scaleH,
        };
    }
}
